import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useAuth } from "./AuthContext";

// ============ CA PARTNER PROGRAM ============

export type PartnerTier = "silver" | "gold" | "platinum";
export type PartnerStatus = "pending" | "active" | "suspended";
export type CommissionStatus = "pending" | "approved" | "paid";
export type PayoutStatus = "requested" | "processing" | "completed";

export interface PartnerClient {
  id: string;
  partnerId: string;
  clientName: string;
  clientEmail: string;
  companyName: string;
  plan: "starter" | "professional" | "enterprise";
  monthlyAmount: number;
  commissionRate: number;
  status: "active" | "churned" | "trial";
  onboardedAt: string;
}

export interface Commission {
  id: string;
  partnerId: string;
  clientId: string;
  clientName: string;
  plan: string;
  subscriptionAmount: number;
  commissionRate: number;
  commissionAmount: number;
  status: CommissionStatus;
  month: string;
  paidAt?: string;
  createdAt: string;
}

export interface Payout {
  id: string;
  partnerId: string;
  amount: number;
  status: PayoutStatus;
  method: "bank_transfer" | "platform_credits";
  requestedAt: string;
  completedAt?: string;
  transactionRef?: string;
}

export interface PartnerProfile {
  id: string;
  userId: string;
  tier: PartnerTier;
  status: PartnerStatus;
  commissionRate: number;
  totalEarned: number;
  totalPaid: number;
  pendingBalance: number;
  clientCount: number;
  joinedAt: string;
}

export interface PartnerStats {
  totalClients: number;
  activeClients: number;
  monthlyRecurringCommission: number;
  totalEarned: number;
  pendingBalance: number;
  totalPaid: number;
  avgClientValue: number;
  churnRate: number;
}

export const COMMISSION_RATES: Record<PartnerTier, number> = {
  silver: 0.15,
  gold: 0.20,
  platinum: 0.25,
};

export const TIER_THRESHOLDS: Record<PartnerTier, { minClients: number; label: string; color: string }> = {
  silver: { minClients: 0, label: "Silver Partner", color: "text-muted-foreground" },
  gold: { minClients: 10, label: "Gold Partner", color: "text-warning" },
  platinum: { minClients: 25, label: "Platinum Partner", color: "text-accent" },
};

const PLAN_PRICES: Record<string, number> = {
  starter: 499,
  professional: 999,
  enterprise: 1999,
};

// Seed data
const SEED_CLIENTS: PartnerClient[] = [];
const SEED_COMMISSIONS: Commission[] = [];
const SEED_PAYOUTS: Payout[] = [];

function computeTier(clientCount: number): PartnerTier {
  if (clientCount >= 25) return "platinum";
  if (clientCount >= 10) return "gold";
  return "silver";
}

interface CAPartnerContextType {
  profile: PartnerProfile;
  clients: PartnerClient[];
  commissions: Commission[];
  payouts: Payout[];
  stats: PartnerStats;
  onboardClient: (name: string, email: string, company: string, plan: string) => void;
  requestPayout: (amount: number, method: "bank_transfer" | "platform_credits") => boolean;
}

const CAPartnerContext = createContext<CAPartnerContextType | null>(null);

export function CAPartnerProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [clients, setClients] = useState<PartnerClient[]>(SEED_CLIENTS);
  const [commissions, setCommissions] = useState<Commission[]>(SEED_COMMISSIONS);
  const [payouts, setPayouts] = useState<Payout[]>(SEED_PAYOUTS);

  const myClients = clients.filter(c => c.partnerId === user?.id);
  const myCommissions = commissions.filter(c => c.partnerId === user?.id);
  const myPayouts = payouts.filter(p => p.partnerId === user?.id);

  const activeClients = myClients.filter(c => c.status === "active");
  const tier = computeTier(activeClients.length);
  const rate = COMMISSION_RATES[tier];

  const totalEarned = myCommissions.reduce((s, c) => s + c.commissionAmount, 0);
  const totalPaid = myPayouts.filter(p => p.status === "completed").reduce((s, p) => s + p.amount, 0);
  const pendingBalance = totalEarned - totalPaid;

  const profile: PartnerProfile = {
    id: `partner-${user?.id}`,
    userId: user?.id || "",
    tier,
    status: "active",
    commissionRate: rate,
    totalEarned,
    totalPaid,
    pendingBalance,
    clientCount: activeClients.length,
    joinedAt: "2024-09-01T00:00:00Z",
  };

  const monthlyRecurring = activeClients.reduce((s, c) => s + c.monthlyAmount * c.commissionRate, 0);

  const stats: PartnerStats = {
    totalClients: myClients.length,
    activeClients: activeClients.length,
    monthlyRecurringCommission: Math.round(monthlyRecurring),
    totalEarned,
    pendingBalance,
    totalPaid,
    avgClientValue: activeClients.length > 0 ? Math.round(activeClients.reduce((s, c) => s + c.monthlyAmount, 0) / activeClients.length) : 0,
    churnRate: myClients.length > 0 ? Math.round((myClients.filter(c => c.status === "churned").length / myClients.length) * 100) : 0,
  };

  const onboardClient = useCallback((name: string, email: string, company: string, plan: string) => {
    if (!user) return;
    const price = PLAN_PRICES[plan] || 499;
    const newClient: PartnerClient = {
      id: `pc-${crypto.randomUUID().slice(0, 8)}`,
      partnerId: user.id,
      clientName: name,
      clientEmail: email,
      companyName: company,
      plan: plan as PartnerClient["plan"],
      monthlyAmount: price,
      commissionRate: rate,
      status: "trial",
      onboardedAt: new Date().toISOString(),
    };
    setClients(prev => [newClient, ...prev]);
  }, [user, rate]);

  const requestPayout = useCallback((amount: number, method: "bank_transfer" | "platform_credits"): boolean => {
    if (amount <= 0 || amount > pendingBalance || !user) return false;
    const payout: Payout = {
      id: `po-${crypto.randomUUID().slice(0, 8)}`,
      partnerId: user.id,
      amount,
      status: "requested",
      method,
      requestedAt: new Date().toISOString(),
    };
    setPayouts(prev => [payout, ...prev]);
    return true;
  }, [pendingBalance, user]);

  return (
    <CAPartnerContext.Provider value={{ profile, clients: myClients, commissions: myCommissions, payouts: myPayouts, stats, onboardClient, requestPayout }}>
      {children}
    </CAPartnerContext.Provider>
  );
}

export function useCAPartner() {
  const ctx = useContext(CAPartnerContext);
  if (!ctx) throw new Error("useCAPartner must be used within CAPartnerProvider");
  return ctx;
}
